import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid2,
  List,
  Stack,
  Typography,
} from "@mui/material";
import {ChevronDown, FileWarning} from "lucide-react";
import {ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {FileTypeLabel, FILE_TYPE_COLORS} from "@/types/FileType.tsx";
import {
  ApprovalStatus,
  FileAnalyzeStatus,
  FileFragmentFragment,
  useAnalyzeZyusetuFilesMutation,
  useGetZyusetuByIdQuery,
  useInactivateFileMutation,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
  ZyusetuFragmentFragment,
} from "@/graphql/graphql";
import {FileInputListItem} from "@/components/FileInputListItem.tsx";
import {PdfViewer} from "@/components/PdfViewer.tsx";
import {useParams, useSearchParams} from "react-router-dom";
import {ZyusetuEditor} from "@/components/pages/Zyusetu/ZyusetuEditor.tsx";
import {usePreloadFiles} from "@/hooks/use-preload-files";
import {DataGridItemVertical} from "@/components/dataDisplay/DataGridItemVertical.tsx";
import {CrossSectionFieldsProvider} from "@/hooks/CrossSectionFieldsProvider.tsx";
import {HighlightedField, SetHighlightedField} from "@/hooks/use-highlight-field";
import {useConfirmDialog} from "@/hooks/use-confirm-dialog";
import {ApprovalStatusChip} from "@/components/pages/Approval/ApprovalStatusChip.tsx";
import {NotFoundPage} from "@/pages/NotFoundPage.tsx";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {LoadingPage} from "@/pages/LoadingPage.tsx";
import {ZyusetuAtom} from "@/plugin/jotai-wrapper.ts";
import {useAtom} from 'jotai'

export function ZyusetuEditPage() {
  const {zyusetuId} = useParams<{ zyusetuId: string }>();
  
  const {data, isLoading} = useGetZyusetuByIdQuery({
    id: zyusetuId!,
  }, {
    refetchOnMount: true,
    retry: 0,
  });
  
  const [zyusetu, setZyusetu] = useAtom(ZyusetuAtom);
  
  useEffect(() => {
    setZyusetu(() => data?.getZyusetuById);
  }, [setZyusetu, data?.getZyusetuById]);
  
  const setHighlightedField = useContext(SetHighlightedField);
  
  // ファイル追加Accordionの開閉状態を管理
  const [fileAddAccordionExpanded, setFileAddAccordionExpanded] = useState(true);
  
  const onFieldFocus = useCallback(
    (value: ZyusetuFieldValueAnalyzedPayloadFragmentFragment) => {
      const file = zyusetu.files.find((file) => file.id === value.analyzedFileId);
      if (!file) {
        return;
      }
      if (!value?.boundingRegions && !value.pageAnchor) {
        return;
      }
      setHighlightedField({field: value, file});
      
      // フィールドにフォーカスが当たったら、ファイル追加Accordionを閉じる
      setFileAddAccordionExpanded(false);
    },
    [setHighlightedField, zyusetu.files],
  );
  
  usePreloadFiles(zyusetu.files);
  
  if (data?.getZyusetuById === null) {
    return <NotFoundPage/>
  }
  
  if (isLoading || data?.getZyusetuById?.id === "" || zyusetu?.id !== zyusetuId) {
    return <LoadingPage/>;
  }
  
  if (!zyusetu || zyusetu?.id !== zyusetuId) {
    return <NotFoundPage/>
  }
  
  return (
    <>
      <Grid2 container spacing={3}>
        <Grid2 size={7}>
          <CrossSectionFieldsProvider>
            <ZyusetuEditor
              editType={zyusetu.approvalStatus === ApprovalStatus.Draft || zyusetu.approvalStatus === ApprovalStatus.Rejected ? ZyusetuEditType.Edit : ZyusetuEditType.ReadOnly}
              onFieldFocus={onFieldFocus}
            />
          </CrossSectionFieldsProvider>
        </Grid2>
        <Grid2 size={5}>
          <Card sx={{position: "sticky", top: "20px", overflow: 'scroll', maxHeight: "100vh", overflowY: 'auto'}}>
            <CardContent>
              <FileAddViewer
                zyusetu={zyusetu}
                expanded={fileAddAccordionExpanded}
                setExpanded={setFileAddAccordionExpanded}
                uploadFileLists={
                  <UploadFileList zyusetuId={zyusetuId}/>
                }/>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </>
  );
}

function FileAddViewer(
  {
    zyusetu,
    uploadFileLists,
    expanded,
    setExpanded
  }: {
    zyusetu: ZyusetuFragmentFragment,
    uploadFileLists: ReactNode,
    expanded: boolean,
    setExpanded: (expanded: boolean) => void
  },
) {
  
  const [searchParam] = useSearchParams();
  const ref = searchParam.get("ref");
  const confirmDialog = useConfirmDialog()
  
  const isCalled = useRef(false);
  useEffect(() => {
    if (isCalled.current) return;
    if (ref === 'new') {
      // ファイルをアップロードさせるモーダルを出す
      confirmDialog.open({
        fullWidth: true,
        icon: <FileWarning color={"error"}/>,
        title: (
          <Typography
            sx={{color: "primary.contrastText"}}
            fontWeight={"bold"}
          >
            Step3: ファイルを追加する
          </Typography>
        ),
        children: (
          <>
            {uploadFileLists}
            <Stack direction={"row"} spacing={2} minWidth={"250px"}>
              <Button
                fullWidth
                variant={'contained'}
                onClick={async () => {
                  confirmDialog.close();
                }}
              >閉じる（実行中でも閉じることが可能です）</Button>
            </Stack>
          </>
        )
      })
      isCalled.current = true;
    }
  }, [confirmDialog, ref, uploadFileLists, zyusetu, zyusetu.files]);
  
  return (<>
    <Accordion defaultExpanded elevation={0} expanded={expanded}>
      <AccordionSummary
        tabIndex={-1}
        expandIcon={<ChevronDown/>}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography>ファイルを追加する</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {uploadFileLists}
        <Grid2 container sx={{mb: 2}}>
          <DataGridItemVertical
            labelWidth={4}
            label={"テンプレート名"}
          >
            {zyusetu.template.title}
          </DataGridItemVertical>
          <DataGridItemVertical
            labelWidth={4}
            label={"承認ステータス"}
          >
            <ApprovalStatusChip status={zyusetu.approvalStatus}/>
          </DataGridItemVertical>
        </Grid2>
      </AccordionDetails>
    </Accordion>
    <FileOverview/>
  </>)
}

function FileOverview() {
  const selectedFile = useContext(HighlightedField)
  return (
    selectedFile?.file ? <Box key={"pdf-viewer"}>
      <Stack
        my={1}
        direction={"row"}
        alignItems={"center"}
        spacing={1}
      >
        <Chip
          sx={{
            backgroundColor: FILE_TYPE_COLORS[selectedFile?.file.fileType],
            color: 'rgba(0, 0, 0, 0.7)',
            fontWeight: 'medium'
          }}
          label={FileTypeLabel[selectedFile?.file.fileType]}
        />
        <Typography>{selectedFile?.file.title}</Typography>
      </Stack>
      <PdfViewer/>
    </Box> : <></>
  )
}

function UploadFileList
({
   zyusetuId
 }: {
  zyusetuId: string
}) {
  const inactivateFile = useInactivateFileMutation();
  const analyzeFile = useAnalyzeZyusetuFilesMutation();
  
  const [zyusetu, setZyusetu] = useAtom(ZyusetuAtom);
  const confirmDialog = useConfirmDialog()
  
  const filesForInputList = useMemo(() => {
    return (
      zyusetu?.template.requiredFileTypes.concat(zyusetu?.template?.optionalFileTypes).map((type) => {
        return {
          type,
          name: FileTypeLabel[type],
          file: zyusetu?.files.find(
            (file) => file.fileType === type,
          ),
        };
      }) ?? []
    );
  }, [zyusetu?.template.requiredFileTypes, zyusetu?.files, zyusetu?.template?.optionalFileTypes]);
  
  const onClickAnalyze = useCallback(
    async (files: FileFragmentFragment[]) => {
      if (!files) return;
      const res = await analyzeFile.mutateAsync(
        {
          fileIds: files.map((file) => file.id),
          zyusetuId: zyusetuId!,
        },
      );
      if (res.analyzeZyusetuFiles) {
        setZyusetu(res.analyzeZyusetuFiles)
      }
    },
    [analyzeFile, setZyusetu, zyusetuId],
  );
  
  const setHighlightedField = useContext(SetHighlightedField);
  
  return (
    <List>
      <Divider/>
      {filesForInputList.map((file, idx) => (
        <>
          <FileInputListItem
            key={file.name}
            onClickDelete={async (file: FileFragmentFragment) => {
              
              confirmDialog.open({
                fullWidth: true,
                icon: <FileWarning color={"error"}/>,
                title: (
                  <Typography
                    sx={{color: "primary.contrastText"}}
                    fontWeight={"bold"}
                  >
                    削除してもよろしいですか?この動作は取り消せません
                  </Typography>
                ),
                children: (
                  <Stack direction={"row"} spacing={2} minWidth={"250px"}>
                    <Button
                      fullWidth
                      variant={'outlined'}
                      onClick={async (_) => confirmDialog.close()}
                    >
                      キャンセル
                    </Button>
                    <Button
                      fullWidth
                      variant={'contained'}
                      onClick={async () => {
                        const result = await inactivateFile.mutateAsync(
                          {id: file.id, zyusetuId: zyusetuId!},
                        );
                        
                        if (result?.inactivateFile)
                          setZyusetu((prev) => {
                            prev.files = prev.files.filter((f) => f.id !== file.id);
                            return prev
                          });
                        confirmDialog.close();
                      }}
                    >削除する</Button>
                  </Stack>
                ),
              });
            }}
            onUploaded={async (file: FileFragmentFragment) => {
              setZyusetu((prev) => {
                prev.files = [...(prev?.files ?? []), {
                  ...file,
                  analyzeStatus: FileAnalyzeStatus.Analyzing
                }]
                return prev
              })
            }}
            onClickAnalyze={onClickAnalyze}
            fileType={file.type}
            title={file.name}
            file={file.file}
            onFileNameClick={(file) => {
              setHighlightedField({
                field: null,
                file,
              })
            }}
            zyusetuId={zyusetuId!}
          />
          {idx !== filesForInputList.length ? <Divider/> : null}
        </>
      ))}
    </List>)
}